import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Pencil, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/primitives";
import { keys } from "@/hooks/queries";
import { api, queryClient, type MetadataOut } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Description and tags, editable.
 *
 * The brief asks for metadata and tags to be straightforward to edit. They were
 * not editable at all: the inspector rendered `keywords` as read-only chips, and
 * the only way to change a description was the "Fix metadata" button, which
 * regenerated it with a model — you could ask for a different description, but
 * you could not write one.
 *
 * Tags are echoed back in their STORED form rather than as typed. A tag entered
 * as "Sparse Autoencoders" is stored as `sparse-autoencoders`, and a panel that
 * kept showing the typed string would quietly disagree with search, the filter
 * and every card.
 */
export function MetadataSection({
  kind,
  itemId,
  /** Only a note can be described from its own body; a document's metadata comes
   * from the ingest pipeline's own enrich pass. */
  canGenerate,
}: {
  kind: "document" | "node";
  itemId: string;
  canGenerate: boolean;
}) {
  const query = useQuery({
    queryKey: keys.metadata(kind, itemId),
    queryFn: () => api.request<MetadataOut>("GET", `/api/metadata/${kind}/${itemId}`),
    enabled: !!itemId,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [tagDraft, setTagDraft] = useState("");

  // Re-seed the draft whenever the item or its stored description changes, so
  // switching selection mid-edit never carries one note's text onto another.
  useEffect(() => {
    setEditing(false);
    setDraft(query.data?.description ?? "");
  }, [itemId, query.data?.description]);

  const save = useMutation({
    mutationFn: (patch: { description?: string; tags?: string[] }) =>
      api.request<MetadataOut>("PATCH", `/api/metadata/${kind}/${itemId}`, patch),
    onSuccess: (result) => {
      queryClient.setQueryData(keys.metadata(kind, itemId), result);
      // Cards show description and tags, so the feed is now stale.
      void queryClient.invalidateQueries({ queryKey: ["notes"] });
      void queryClient.invalidateQueries({ queryKey: keys.node(itemId) });
    },
    onError: (error) => toast.error("Couldn't save", { description: String(error) }),
  });

  const generate = useMutation({
    mutationFn: () =>
      api.request<MetadataOut & { enriched: boolean }>(
        "POST",
        `/api/metadata/node/${itemId}/generate`,
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(keys.metadata(kind, itemId), result);
      void queryClient.invalidateQueries({ queryKey: ["notes"] });
      toast[result.enriched ? "success" : "message"](
        result.enriched ? "Description and tags generated" : "Not enough written yet to describe",
      );
    },
    onError: (error) => toast.error("Couldn't generate", { description: String(error) }),
  });

  const data = query.data;
  const tags = data?.tags ?? [];

  const addTag = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    setTagDraft("");
    save.mutate({ tags: [...tags, value] });
  };

  return (
    <section className="space-y-2.5">
      <div className="flex items-center gap-1.5">
        <h3 className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Metadata
        </h3>
        {canGenerate && (
          <Button
            size="md"
            variant="ghost"
            className="ml-auto h-7 gap-1.5 px-2 text-[12px] text-muted-foreground hover:text-foreground"
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
            title="Write a description and tags from this note's own text. Never overwrites what you wrote."
          >
            <Sparkles className="h-3.5 w-3.5" />
            {generate.isPending ? "Generating…" : "Generate"}
          </Button>
        )}
      </div>

      {/* Description */}
      {editing ? (
        <div className="space-y-1.5">
          <Textarea
            autoFocus
            rows={5}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setEditing(false);
                setDraft(data?.description ?? "");
              }
            }}
            placeholder="What is this, and what is it useful for?"
          />
          <div className="flex gap-1.5">
            <Button
              size="md"
              className="flex-1"
              disabled={save.isPending}
              onClick={() => {
                setEditing(false);
                save.mutate({ description: draft });
              }}
            >
              <Check className="h-4 w-4" /> Save
            </Button>
            <Button
              size="md"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setDraft(data?.description ?? "");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="group/desc flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/60"
        >
          <span
            className={cn(
              "flex-1 text-[13.5px] leading-relaxed",
              data?.description ? "text-muted-foreground" : "italic text-muted-foreground/55",
            )}
          >
            {data?.description || "No description yet — click to write one"}
          </span>
          <Pencil className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/desc:opacity-100" />
        </button>
      )}

      {/* Tags */}
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="group/tag flex items-center justify-center gap-1.5 rounded-full border border-border px-2 py-0.5 font-mono text-[11px] leading-[1.6] text-muted-foreground"
          >
            {/* A spacer the same width as the remove button. The button holds
                its slot whether or not it is showing (it fades in on hover), so
                without this the label sits permanently off-centre, pushed left
                by an invisible control. */}
            <span aria-hidden className="w-3 shrink-0" />
            <span className="text-center">{tag}</span>
            <button
              type="button"
              aria-label={`Remove tag ${tag}`}
              disabled={save.isPending}
              onClick={() => save.mutate({ tags: tags.filter((t) => t !== tag) })}
              className="w-3 shrink-0 opacity-0 transition-opacity hover:text-destructive group-hover/tag:opacity-100 focus-visible:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <Input
          value={tagDraft}
          onChange={(e) => setTagDraft(e.target.value)}
          onKeyDown={(e) => {
            // Comma and Enter both commit — people type tag lists both ways.
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addTag(tagDraft);
            }
            // Backspace on an empty box removes the last tag, as in every tag
            // input anyone has used.
            if (e.key === "Backspace" && !tagDraft && tags.length) {
              save.mutate({ tags: tags.slice(0, -1) });
            }
          }}
          onBlur={() => addTag(tagDraft)}
          placeholder={tags.length ? "add tag…" : "add a tag…"}
          aria-label="Add a tag"
          className="h-7 w-[104px] flex-1 border-dashed px-2 font-mono text-[11px]"
        />
      </div>
      {query.isError && (
        <p className="text-[12px] text-destructive/80">Couldn&apos;t load metadata.</p>
      )}
    </section>
  );
}
